import { mapGetters } from 'vuex'

/**
 * SRR-2576 / SRR-2578 / SRR-2580 / SRR-2581 — role-conditional wording for the
 * "client"/"brand" concept across My Account and campaign-adjacent screens.
 *
 * SRR-2576 shipped a flat rename (every role sees "Brand") which broke the
 * Traffic Manager's own "Customers" wording and leaked "Agency employees"
 * onto Brand Admin/Employee screens that should read "Brand employees". This
 * mixin replaces the flat literals with a role bucket so each audience keeps
 * (or regains) its own wording from one source of truth.
 *
 * Strings are plain literals, not i18n keys — matching how SRR-2576 was
 * already implemented, and ro.json is already missing translations for the
 * keys being replaced here, so this is no regression.
 */

// One row per audience bucket, one column per UI slot. `null` marks a slot
// that is never rendered for that bucket (e.g. Traffic Manager has no
// "employees" screens) so a stray render surfaces as empty rather than as
// wording borrowed from another audience.
const NAMING = Object.freeze({
  agency: Object.freeze({
    clientsNav: 'Brands',
    clientsPageTitle: 'Brands',
    clientsNewButton: 'New Brand',
    clientsCreateModalTitle: 'Create Brand',
    employeesNav: 'Agency employees',
    employeesPageTitle: 'Agency employees',
    employeesInviteButton: 'Invite agency employee',
    employeesCreateModalTitle: 'Create agency employee account',
  }),

  brand: Object.freeze({
    clientsNav: 'Brands',
    clientsPageTitle: 'Brands',
    clientsNewButton: 'New Brand',
    clientsCreateModalTitle: 'Create Brand',
    employeesNav: 'Brand employees',
    employeesPageTitle: 'Brand employees',
    employeesInviteButton: 'Invite Brand employee',
    employeesCreateModalTitle: 'Create Brand employee account',
  }),

  'traffic-manager': Object.freeze({
    clientsNav: 'Customers',
    clientsPageTitle: 'Customers',
    clientsNewButton: 'New Customer',
    clientsCreateModalTitle: 'Create customer',
    employeesNav: null,
    employeesPageTitle: null,
    employeesInviteButton: null,
    employeesCreateModalTitle: null,
  }),

  // Everything else — must carry exactly what shipped on staging today so no
  // role loses existing wording (agency-client, publisher, admin, etc.).
  default: Object.freeze({
    clientsNav: 'Brands',
    clientsPageTitle: 'Brands',
    clientsNewButton: 'New Brand',
    clientsCreateModalTitle: 'Create Brand',
    employeesNav: 'Agency employees',
    employeesPageTitle: 'Account users',
    employeesInviteButton: 'Invite agency employee',
    employeesCreateModalTitle: 'Create agency employee account',
  }),
})

// Pure function version of the bucket logic below, usable outside a component
// instance (router guards run before any component mounts, so there's no
// `this.$isAgency()` available there — only the store directly). Both the
// entityAudience computed and the router's redirect guards call this, so the
// bucket rules never drift out of sync between "what text renders" and
// "which URL you land on".
export function resolveEntityAudience(userType, isAgency) {
  if (userType === 'traffic-manager') {
    return 'traffic-manager'
  }

  if (['agency', 'enterprise'].includes(userType)) {
    return 'agency'
  }

  // agency-employee/employee only count as "agency" wording when their
  // parent account actually is an agency — isAgency() is the same check
  // $isAgency() makes (vue/src/plugins/roleAndAccess.js), passed in so this
  // function doesn't need a component instance to call it.
  if (['agency-employee', 'employee'].includes(userType) && isAgency()) {
    return 'agency'
  }

  if (['brand-admin', 'brand-employee'].includes(userType)) {
    return 'brand'
  }

  return 'default'
}

export default {
  computed: {
    // Aliased away from `user_type`: several target components (Account.vue,
    // AccountClients.vue, EditUser.vue, EditClient.vue) already map that name
    // themselves, and a clashing mapGetters key would be silently overridden
    // by the component's own computed.
    ...mapGetters({
      entityNamingUserType: 'user/type',
    }),

    entityAudience() {
      return resolveEntityAudience(this.entityNamingUserType, () => this.$isAgency())
    },

    entityNaming() {
      return NAMING[this.entityAudience]
    },
  },
}
